import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePermissions } from '../../services';


import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBan, faEye, faTrash, faUserCheck, faUserShield } from '@fortawesome/free-solid-svg-icons';
import { GroupType, ProcessRoleType, UserType, ColumnsType } from '../../types';
import { useAppContext } from '../../Context';
import { useAppSelector, useAppDispatch } from '../../hooks';

import { setLoading, setPopConfirmation } from '../../slices/miscSlice';
import { setUser, setUsers, initialStateUsers } from '../../slices/usersSlice';

import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid } from '@mui/material';

import { Button } from '../../components/shared/FormElements';
import DataTable from '../../components/shared/DataTable';
import { constants } from '../../constants';

const ViewUsers : React.FC = () => {

    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { apiCall } = useAppContext();

    const { canAdd_user, canView_user, canView_menu_users, canDelete_user, canChange_user } = usePermissions([
        'add_user',
        'view_user',
        'view_menu_users',
        'delete_user',
        'change_user'
    ]);
    const users : Array<UserType> = useAppSelector((state) => state.users.users);

    const [permissionsOpen, setPermissionsOpen] = useState(false);
    const [permissionsLoading, setPermissionsLoading] = useState(false);
    const [permissionsUser, setPermissionsUser] = useState<UserType | null>(null);
    const [permissionsCache, setPermissionsCache] = useState<Record<number, UserType>>({});

    useEffect(() => {
        if (!canView_user && !canView_menu_users) {
            navigate('/');
            return;
        }

        if (users.length === 0) {
            getUsers();   // get users
        }

        dispatch(setUser(initialStateUsers.user)); // clear user
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getUsers = async () => {
        dispatch(setLoading(true));
        const response = await apiCall('GET', 'users/', null, '', 'Error al obtener los usuarios');
        dispatch(setLoading(false));
        if (response) {
            dispatch(setUsers(response));
        }
    }

    const handleOpenPermissions = async (user : UserType) => {
        if (user.is_admin_group) {
            return;
        }
        setPermissionsOpen(true);
        setPermissionsUser(null);
        setPermissionsLoading(false);

        if (permissionsCache[user.id]) {
            setPermissionsUser(permissionsCache[user.id]);
            return;
        }

        setPermissionsLoading(true);
        const response = await apiCall('GET', `user/${user.id}/`, null, '', 'Error al obtener permisos del usuario');
        setPermissionsLoading(false);
        if (response) {
            setPermissionsUser(response);
            setPermissionsCache((prev) => ({ ...prev, [user.id]: response }));
        }
    }

    const handleClosePermissions = () => {
        setPermissionsOpen(false);
        setPermissionsUser(null);
    }



    const actions : Array<any> = [
        canView_user ? {icon: <FontAwesomeIcon icon={faEye} />, tooltip: 'Ver', onClick: (rowData :UserType) => handleViewUser(rowData)} : null,
        canDelete_user ? {icon: <FontAwesomeIcon icon={faTrash} />, tooltip: 'Eliminar', onClick: ( rowData :any) => handleDeleteUser (rowData)} : null,
        canChange_user ? {icon: { field: "status", options: { true : <FontAwesomeIcon icon={faBan} />, false: <FontAwesomeIcon icon={faUserCheck} />}} , tooltip: { field: "status" , options : {true: 'Desactivar', false: 'Activar'}}, onClick: (rowData :any) => handleActiveOrInactiveUser(rowData)} : null,
    ];

    const handleViewUser = (user : UserType) => {
        setUser(user);
        navigate(`/usuario/${user.id}`);
    }

    const handleDeleteUser = (user : UserType) => {
        dispatch(setPopConfirmation({show: true, message: '¿Está seguro que desea eliminar el usuario?', alertType: 'danger', okFunc: () => deleteUser(user.id) }));
    }

    const deleteUser = async (id : number) => {
        dispatch(setLoading(true));
        const response = await apiCall('DELETE', `user/${id}/`, null, 'Usuario eliminado', 'Error al eliminar el usuario');
        dispatch(setLoading(false));
        if (response) {
            dispatch(setUsers(users.filter((user : UserType) => user.id !== id)));
        }
    }

    const handleActiveOrInactiveUser = (user : UserType) => {
        const textMessage = user.status ? '¿Está seguro que desea desactivar el usuario?' : '¿Está seguro que desea activar el usuario?';
        dispatch(setPopConfirmation({show: true, message: textMessage, alertType: 'danger', okFunc: () => banUser(user.id) }));
    }

    const banUser = async (id : number) => {
        dispatch(setLoading(true));

        const response = await apiCall('PATCH', `user/${id}/toggleStatus/`, null, 'Usuario modificado', 'Error al modificar el usuario');
        dispatch(setLoading(false));
        if (response) {
            dispatch(setUsers(users.map((user : UserType) => user.id === id ? {...user, status: !user.status} : user)));
        }
    }

    const columns : Array<ColumnsType> = [
        {title: 'Nombre', field: 'name'},
        {title: 'Usuario', field: 'username'},
        {title: 'E-Mail', field: 'email'},
        canView_user ? {
            title: 'Permisos',
            field: 'id',
            type: 'icon_action',
            icon: (row: UserType) => (row.is_admin_group ? 'Acceso Total' : <FontAwesomeIcon icon={faUserShield} />),
            tooltip: 'Ver grupos y permisos',
            onClick: (rowData :UserType) => handleOpenPermissions(rowData),
            disabledWhenField: 'is_admin_group',
            disabledWhenValue: true,
        } : null,
        {title: 'Estado', field: 'status', type: 'decorator_bool', cell_class : 'text-center' },
    ].filter(Boolean) as Array<ColumnsType>;

    const formatPermissionLabel = (permission: string | undefined) => {
        if (!permission) {
            return '-';
        }
        const raw = permission.includes('.') ? permission.split('.').pop() || permission : permission;
        return constants.permission_labels[raw] || raw;
    }

    const renderPermissionList = (items: Array<any> | undefined) => {
        if (!items || items.length === 0) {
            return <p>No registra</p>;
        }
        return (
            <ul style={{ paddingLeft: '1.2rem', marginTop: 0 }}>
                {items.map((item, index) => {
                    const raw = typeof item === 'string' ? item : item?.name;
                    const text = formatPermissionLabel(raw);
                    return <li key={`${text}-${index}`}>{text ? text : '-'}</li>;
                })}
            </ul>
        );
    }

    const renderGroups = (groups: Array<GroupType> | undefined) => {
        if (!groups || groups.length === 0) {
            return <p>No registra</p>;
        }
        return (
            <ul style={{ paddingLeft: '1.2rem', marginTop: 0 }}>
                {groups.map((group) => (
                    <li key={group.id}>
                        <span>{group.name}</span>
                        {group.permissions && group.permissions.length > 0 ? (
                            <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                {group.permissions.map((permission) => formatPermissionLabel(permission)).join(', ')}
                            </div>
                        ) : null}
                    </li>
                ))}
            </ul>
        );
    }

    const renderProcessRoles = (roles: Array<ProcessRoleType> | undefined) => {
        if (!roles || roles.length === 0) {
            return <p>No registra</p>;
        }
        return (
            <ul style={{ paddingLeft: '1.2rem', marginTop: 0 }}>
                {roles.map((role) => (
                    <li key={role.id}>{role.name}</li>
                ))}
            </ul>
        );
    }

    return <div className="container-wrap">
    <Grid container spacing={2}>
        <Grid item xs={12} sm={10} className="text-left">
            <h2 className="text-left">Usuarios</h2>
        </Grid>
        <Grid item xs={12} sm={2} className="text-right">
            {canAdd_user && <Button className="btn btn-primary" onClick={() => navigate('/usuario/crear')}>Agregar</Button>}
        </Grid>
    </Grid> 
    <DataTable data={users} columns={columns} actions={actions} />
    <Dialog open={permissionsOpen} onClose={handleClosePermissions} fullWidth maxWidth="sm">
        <DialogTitle>Grupos y permisos</DialogTitle>
        <DialogContent>
            {permissionsLoading ? (
                <div className="text-center">
                    <CircularProgress size={28} />
                </div>
            ) : permissionsUser ? (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <div className="form-group">
                            <label>Usuario</label>
                            <p>{permissionsUser.name ? permissionsUser.name : permissionsUser.username}</p>
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        <h3>Roles</h3>
                    </Grid>
                    <Grid item xs={12}>
                        <div className="form-group">
                            <label style={{ fontSize: '0.9rem', fontWeight: 600 }}>Grupos</label>
                            {renderGroups(permissionsUser.groups)}
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        <div className="form-group">
                            <label style={{ fontSize: '0.9rem', fontWeight: 600 }}>Permisos</label>
                            {renderPermissionList(permissionsUser.permissions)}
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <div className="form-group">
                            <h3>Roles de Procesos</h3>
                            <label style={{ fontSize: '0.9rem', fontWeight: 600 }}>Roles de procesos</label>
                            {renderProcessRoles(permissionsUser.process_roles)}
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        <div className="form-group">
                            <label style={{ fontSize: '0.9rem', fontWeight: 600 }}>Permisos de procesos</label>
                            {renderPermissionList(permissionsUser.process_permissions)}
                        </div>
                    </Grid>
                </Grid>
            ) : (
                <p>No hay datos para mostrar.</p>
            )}
        </DialogContent>
        <DialogActions>
            <Button className="btn btn-primary" onClick={handleClosePermissions}>Cerrar</Button>
        </DialogActions>
    </Dialog>
</div>;

}

export default ViewUsers;
